import { useIntl } from 'react-intl';
import { ActivityType } from 'app/data/types/activity';
import { cn } from '@hqo/react-hooks';
import { getStatusMeta, humanizeStatusKey, resolveStatusDotColor } from 'app/shared/consts/activity';

interface ActivityStatusPillProps {
  statusKey: string;
  /** Which vertical the row is, so a visit can wear visitor-registration's palette. */
  type?: ActivityType;
  /** BE-localized label (work-order rows only). Falls back to `localize(statusKey)`. */
  statusLabel?: string;
  /** Building-configured palette token (work-order rows only). Overrides the semantic tone. */
  statusColor?: string;
  className?: string;
}

/**
 * Status pill — a semantic colored dot + neutral label. The dot color comes from the status TONE
 * (semantic, independent of the tenant accent); the label is `statusLabel ?? localize(statusKey)`,
 * with an unknown key humanized so it never renders blank.
 */
export const ActivityStatusPill = ({
  statusKey,
  type,
  statusLabel,
  statusColor,
  className,
}: ActivityStatusPillProps): JSX.Element => {
  const intl = useIntl();
  const meta = getStatusMeta(statusKey);
  const label = statusLabel
    ?? (meta.labelId ? intl.formatMessage({ id: meta.labelId }) : humanizeStatusKey(statusKey));
  // A building that configured a colour for its own status wins over our semantic tone — the same
  // `in-progress` status is orange in most buildings, yellow or blue in others.
  const dotColor = resolveStatusDotColor(statusKey, statusColor, type);

  return (
    <span
      className={cn('inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-semibold text-system-grey-dark', className)}
      data-testid="activity-status-pill"
    >
      <span className="size-[7px] shrink-0 rounded-full" style={{ backgroundColor: dotColor }} />
      {label}
    </span>
  );
};
